import type { QueryClient } from "@tanstack/react-query";
import { preflightKeys } from "@/lib/queries";
import type { PreflightMarketsList } from "../types/preflight.types";

/**
 * Bump a market's membership count in the cached list so the hub empty states
 * and selector counts track create/delete without waiting on a markets refetch.
 */
export function bumpMarketReportCount(
  queryClient: QueryClient,
  tenantId: string,
  marketId: string,
  delta: number,
): void {
  queryClient.setQueryData<PreflightMarketsList>(
    preflightKeys.markets(tenantId),
    (current) =>
      current
        ? {
            ...current,
            markets: current.markets.map((market) =>
              market.id === marketId
                ? {
                    ...market,
                    reportCount: Math.max(0, market.reportCount + delta),
                  }
                : market,
            ),
          }
        : current,
  );
}

/** Bump the unique All Markets count once per created or deleted report. */
export function bumpTenantReportCount(
  queryClient: QueryClient,
  tenantId: string,
  delta: number,
): void {
  queryClient.setQueryData<PreflightMarketsList>(
    preflightKeys.markets(tenantId),
    (current) =>
      current
        ? { ...current, reportCount: Math.max(0, current.reportCount + delta) }
        : current,
  );
}

/**
 * Drop every markets list and insights scope for the tenant. Report create/delete
 * and market writes all change what the hub aggregate covers (KTD14, R17).
 */
export function invalidateMarketAggregates(
  queryClient: QueryClient,
  tenantId: string,
): void {
  void queryClient.invalidateQueries({ queryKey: preflightKeys.markets(tenantId) });
  void queryClient.invalidateQueries({
    queryKey: preflightKeys.insightsScopes(tenantId),
  });
}
